import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

const AdminAuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SECURITY MODEL — JWT is stored in an httpOnly, Secure, SameSite=Lax cookie
 * (`admin_token`) set by the backend on POST /api/admin/login.
 *
 * The frontend NEVER reads the raw token. All API calls send the cookie via
 * `withCredentials: true`. On mount we verify the session by hitting
 * /api/admin/me — a 200 means the cookie is valid; anything else means we
 * are anonymous.
 *
 * Logout POSTs /api/admin/logout to have the backend delete the cookie and
 * then clears local state. There is no localStorage/sessionStorage access
 * anywhere in this file — the token is unreachable from JavaScript, which
 * removes the XSS token-exfiltration vector entirely.
 *
 * Backend CORS uses specific origins + allow_credentials=True (see
 * backend/.env CORS_ORIGINS) — wildcard origins are rejected by browsers
 * when cookies are involved, which is enforced by the browser itself.
 */

// A minimal axios instance that ALWAYS sends cookies to the admin API.
const adminApi = axios.create({
  baseURL: API,
  withCredentials: true,
});

export const AdminAuthProvider = ({ children }) => {
  // null = checking session, false = anonymous, object = authenticated admin
  const [admin, setAdmin] = useState(null);

  const checkSession = useCallback(async () => {
    try {
      const { data } = await adminApi.get('/api/admin/me');
      setAdmin(data);
    } catch (e) {
      // 401 = anonymous (expected). Anything else is worth surfacing in dev.
      if (process.env.NODE_ENV !== 'production' && e?.response?.status !== 401) {
        console.warn('[admin] session check failed:', e?.response?.status || e?.message);
      }
      setAdmin(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = useCallback(async (email, password) => {
    const { data } = await adminApi.post('/api/admin/login', { email, password });
    // Cookie is set by the backend response; we only track identity in state.
    setAdmin({ email: data.email, role: 'admin' });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await adminApi.post('/api/admin/logout');
    } catch (e) {
      // Backend clears the cookie regardless; log for visibility only.
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[admin] logout call failed (state will still be cleared):', e?.response?.status || e?.message);
      }
    }
    setAdmin(false);
  }, []);

  const authedFetch = useCallback(async (method, path, body) => {
    try {
      const { data } = await adminApi.request({
        method,
        url: path,
        data: body,
      });
      return data;
    } catch (e) {
      if (e?.response?.status === 401) {
        setAdmin(false);
      }
      throw e;
    }
  }, []);

  const value = useMemo(
    () => ({ admin, login, logout, authedFetch }),
    [admin, login, logout, authedFetch],
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
};
